import {
  Affix,
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography
} from 'antd'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import dayjs from 'dayjs'

const { Title, Text } = Typography
const { RangePicker } = DatePicker
const { Option } = Select

const AssessmentOptionsMenu = (props) => {
  const [form] = Form.useForm()
  const { courseId } = useParams()
  const navigate = useNavigate()
  const [startValue, setStartValue] = useState(null)

  const {
    handleSubmit,
    maxScore,
    controlledQuestionType,
    handlecontrolledQuestionType,
    assessmentType
  } = props

  useEffect(() => {
    form.setFieldsValue({
      maxScore,
      questionsType: controlledQuestionType,
      submissionType:
        controlledQuestionType === 'file'
          ? 'written'
          : form.getFieldValue('submissionType')
    })
  }, [form, maxScore, controlledQuestionType])

  return (
    <Affix offsetTop={10}>
      <div
        style={{
          backgroundColor: 'white',
          padding: '20px 20px',
          borderRadius: '10px'
        }}
      >
        <Title level={4}>{assessmentType} Options</Title>
        <Divider />
        <Form
          form={form}
          onFinish={handleSubmit}
          name="options"
          layout={'vertical'}
        >
          <Form.Item
            name="questionsType"
            style={{ width: '60%' }}
            label={<Text strong>Questions Type</Text>}
          >
            <Select onChange={handlecontrolledQuestionType}>
              <Option value="online">Add Questions Online</Option>
              <Option value="file">Attach File</Option>
            </Select>
          </Form.Item>
          <Form.Item
            initialValue="online"
            name="submissionType"
            style={{ width: '60%' }}
            label={<Text strong>Submission Type</Text>}
          >
            <Select disabled={controlledQuestionType === 'file'}>
              <Option value="online">Online</Option>
              <Option value="written">Upload Written File</Option>
            </Select>
          </Form.Item>
          {assessmentType === 'Exam' && (
            <Form.Item
              name="time"
              label={<Text strong>Open & Close Time</Text>}
              rules={[
                {
                  type: 'array',
                  required: true,
                  message: 'Please select date and time'
                }
              ]}
            >
              <RangePicker
                use12Hours
                placeholder={['OpenTime', 'CloseTime']}
                showTime={{ format: 'HH:mm:ss' }}
                format="YYYY-MM-DD HH:mm:ss"
                disabledDate={(current) => {
                  return current && current < dayjs().startOf('day')
                }}
                onCalendarChange={(dates) => {
                  if (dates && dates[0]) {
                    setStartValue(dates[0]) // save start time
                  }
                }}
                disabledTime={(date, type) => {
                  const now = dayjs()

                  // START restrictions (disable past times of today)
                  if (type === 'start' && date && date.isSame(now, 'day')) {
                    return {
                      disabledHours: () =>
                        [...Array(24).keys()].filter((h) => h < now.hour()),
                      disabledMinutes: (h) =>
                        h === now.hour()
                          ? [...Array(60).keys()].filter(
                              (m) => m < now.minute()
                            )
                          : [],
                      disabledSeconds: (h, m) =>
                        h === now.hour() && m === now.minute()
                          ? [...Array(60).keys()].filter(
                              (s) => s < now.second()
                            )
                          : []
                    }
                  }

                  // END restrictions (must be >= start time)
                  if (
                    type === 'end' &&
                    startValue &&
                    date &&
                    date.isSame(startValue, 'day')
                  ) {
                    return {
                      disabledHours: () =>
                        [...Array(24).keys()].filter(
                          (h) => h < startValue.hour()
                        ),
                      disabledMinutes: (h) =>
                        h === startValue.hour()
                          ? [...Array(60).keys()].filter(
                              (m) => m < startValue.minute()
                            )
                          : [],
                      disabledSeconds: (h, m) =>
                        h === startValue.hour() && m === startValue.minute()
                          ? [...Array(60).keys()].filter(
                              (s) => s < startValue.second()
                            )
                          : []
                    }
                  }

                  return {}
                }}
              />
            </Form.Item>
          )}

          {assessmentType === 'Assignment' && (
            <Form.Item
              name="dueDate"
              style={{ width: '60%' }}
              rules={[
                {
                  type: 'object',
                  required: true,
                  message: 'Please select date and time'
                }
              ]}
              label={<Text strong>Due Date</Text>}
            >
              <DatePicker
                format="YYYY-MM-DD HH:mm:ss"
                showTime
                disabledDate={(current) =>
                  current && current < dayjs().startOf('day')
                }
                disabledTime={(date) => {
                  const now = dayjs()
                  if (date && date.isSame(now, 'day')) {
                    return {
                      disabledHours: () =>
                        [...Array(24).keys()].filter((h) => h < now.hour()),
                      disabledMinutes: (h) =>
                        h === now.hour()
                          ? [...Array(60).keys()].filter(
                              (m) => m < now.minute()
                            )
                          : [],
                      disabledSeconds: (h, m) =>
                        h === now.hour() && m === now.minute()
                          ? [...Array(60).keys()].filter(
                              (s) => s < now.second()
                            )
                          : []
                    }
                  }
                  return {}
                }}
              />
            </Form.Item>
          )}

          <Form.Item
            name="weight"
            label={<Text strong>Course Weight</Text>}
            rules={[
              { required: true, message: 'Please enter course weight' },
              {
                validator: (_, value) => {
                  if (value === undefined || value === '')
                    return Promise.resolve()
                  const num = parseFloat(value)
                  if (num >= 0 && num <= 1) return Promise.resolve()
                  return Promise.reject(
                    new Error('Weight must be between 0 and 1')
                  )
                }
              }
            ]}
          >
            <Input style={{ width: '25%' }} placeholder="eg. 0.2" />
          </Form.Item>
          <Form.Item
            name="maxScore"
            style={{ width: '60%' }}
            label={<Text strong>Max Score</Text>}
          >
            <InputNumber disabled={controlledQuestionType === 'online'} />
          </Form.Item>

          <Space style={{ marginTop: '8px' }}>
            <Button
              onClick={() => {
                form.submit()
              }}
              type="primary"
            >
              Create
            </Button>
            <Button
              onClick={() =>
                navigate(
                  `/app/course/${courseId}/${assessmentType.toLowerCase()}s`
                )
              }
            >
              Cancel
            </Button>
          </Space>
        </Form>
      </div>
    </Affix>
  )
}

export default AssessmentOptionsMenu
